"use client";

import React from "react";
import Image from "next/image";
import {
  getImageUrl,
  isValidImageUrl,
  getPlaceholderImage,
} from "@/utils/image";

export interface InfoCardProps {
  // Image props
  image: string;
  imageAlt?: string;

  // Content props
  title: string;
  description: string;

  // Layout options
  reverse?: boolean;

  // Additional styling
  className?: string;

  // Local image
  localImage?: boolean;
}

const InfoCard: React.FC<InfoCardProps> = ({
  image,
  imageAlt = "",
  title,
  description,
  reverse = false,
  className = "",
  localImage = false,
}) => {
  // Get the processed image URL
  let imageSrc: string;

  if (localImage) {
    imageSrc = image;
  } else {
    const processedImageUrl = getImageUrl(image);
    imageSrc = isValidImageUrl(processedImageUrl)
      ? processedImageUrl
      : getPlaceholderImage(320, 200, "Info");
  }

  return (
    <div
      className={`flex flex-col md:flex-row ${
        reverse ? "md:flex-row-reverse" : ""
      } items-start gap-4 md:gap-6 lg:gap-8 ${className}`}
    >
      {/* Left side - Image */}
      <div className="flex-shrink-0 w-full md:w-auto">
        <div
          className="relative rounded-2xl overflow-hidden shadow-lg"
          style={{ width: "100%", maxWidth: 320, height: "auto", aspectRatio: "16/10" }}
        >
          <Image
            src={imageSrc}
            alt={imageAlt || "Info image"}
            className="object-cover w-full h-full"
            width={320}
            height={200}
          />
        </div>
      </div>

      {/* Right side - Content */}
      <div className="flex-1 space-y-4">
        <h2 className="text-sm md:text-base lg:text-lg font-bold text-gray-800">{title}</h2>

        <div
          className="text-xs md:text-sm lg:text-base text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>
    </div>
  );
};

export default InfoCard;
